import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { lcApiBase, readObservableError } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface LcBonReceptionEntreeLigne {
  code_barre?: string | null;
  ref_interne?: string | null;
  qnt: number;
  libelle?: string | null;
}

export type LcStockMouvementType = 'entree' | 'sortie';

/**
 * Stock logiciel-caisse identifié par code-barres (pas product_id ERP).
 */
@Injectable({ providedIn: 'root' })
export class LogicielCaisseStockReceptionService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  /**
   * POST /api/stocks/bon-reception-entree
   * Entrée stock par code-barres (idempotente via reference_document).
   */
  entreeByBarcode(params: {
    entrepot_id: number;
    reference_document?: string | null;
    lignes: LcBonReceptionEntreeLigne[];
  }): Observable<{ success: true; updated: number; already_applied?: boolean }> {
    return this.ensureSession().pipe(
      switchMap(() => {
        const entrepotId = Number(params.entrepot_id);
        if (!Number.isFinite(entrepotId) || entrepotId <= 0) {
          return throwError(() => new Error('Entrepôt logiciel caisse invalide.'));
        }
        const lignes = (params.lignes || [])
          .map((l) => ({
            code_barre: this.cleanBarcode(l.code_barre),
            ref_interne: this.cleanRef(l.ref_interne),
            qnt: Math.abs(Number(l.qnt) || 0),
            libelle: l.libelle != null ? String(l.libelle).trim() || null : null,
          }))
          .filter((l) => l.qnt > 0 && (l.code_barre || l.ref_interne));

        if (!lignes.length) {
          return throwError(
            () =>
              new Error(
                'Aucune ligne avec code-barres / référence. Ajoutez le code-barres sur chaque produit.'
              )
          );
        }

        const url = `${lcApiBase(this.authService)}/api/stocks/bon-reception-entree`;
        return this.http
          .post<{
            success?: boolean;
            updated?: number;
            already_applied?: boolean;
            error?: string;
            message?: string;
          }>(
            url,
            {
              entrepot_id: entrepotId,
              reference_document: params.reference_document || null,
              lignes,
            },
            { headers: this.authHeaders() }
          )
          .pipe(
            map((res) => {
              if (!res?.success) {
                throw new Error(res?.error || res?.message || 'Entrée stock caisse refusée');
              }
              return {
                success: true as const,
                updated: Number(res.updated) || 0,
                already_applied: !!res.already_applied,
              };
            }),
            catchError((err: unknown) => {
              if (err instanceof Error && !(err instanceof HttpErrorResponse)) {
                return throwError(() => err);
              }
              return throwError(
                () => new Error(readObservableError(err, 'Échec entrée stock par code-barres.'))
              );
            })
          );
      })
    );
  }

  /**
   * POST /api/stocks/bon-livraison-sortie
   * Sortie stock par code-barres : −stock_physique et −stock_disponible (idempotent via reference_document).
   */
  sortieBonLivraisonByBarcode(params: {
    entrepot_id: number;
    reference_document?: string | null;
    lignes: LcBonReceptionEntreeLigne[];
  }): Observable<{ success: true; updated: number; already_applied?: boolean }> {
    return this.ensureSession().pipe(
      switchMap(() => {
        const entrepotId = Number(params.entrepot_id);
        if (!Number.isFinite(entrepotId) || entrepotId <= 0) {
          return throwError(() => new Error('Entrepôt logiciel caisse invalide.'));
        }
        const lignes = (params.lignes || [])
          .map((l) => ({
            code_barre: this.cleanBarcode(l.code_barre),
            ref_interne: this.cleanRef(l.ref_interne),
            qnt: Math.abs(Number(l.qnt) || 0),
            libelle: l.libelle != null ? String(l.libelle).trim() || null : null,
          }))
          .filter((l) => l.qnt > 0 && (l.code_barre || l.ref_interne));

        if (!lignes.length) {
          return throwError(
            () =>
              new Error(
                'Aucune ligne avec code-barres / référence. Ajoutez le code-barres sur chaque produit.'
              )
          );
        }

        const url = `${lcApiBase(this.authService)}/api/stocks/bon-livraison-sortie`;
        return this.http
          .post<{
            success?: boolean;
            updated?: number;
            already_applied?: boolean;
            error?: string;
            message?: string;
          }>(
            url,
            {
              entrepot_id: entrepotId,
              reference_document: params.reference_document || null,
              lignes,
            },
            { headers: this.authHeaders() }
          )
          .pipe(
            map((res) => {
              if (!res?.success) {
                throw new Error(res?.error || res?.message || 'Sortie stock caisse refusée');
              }
              return {
                success: true as const,
                updated: Number(res.updated) || 0,
                already_applied: !!res.already_applied,
              };
            }),
            catchError((err: unknown) => {
              if (err instanceof Error && !(err instanceof HttpErrorResponse)) {
                return throwError(() => err);
              }
              return throwError(
                () => new Error(readObservableError(err, 'Échec sortie stock bon de livraison.'))
              );
            })
          );
      })
    );
  }

  /**
   * PUT /api/stocks/by-code-barre — délègue à bon-reception-entree pour les entrées.
   */
  mouvementByBarcode(params: {
    entrepot_id: number;
    type_mouvement: LcStockMouvementType;
    motif?: string | null;
    reference_document?: string | null;
    lignes: LcBonReceptionEntreeLigne[];
  }): Observable<{ success: true; updated: number; already_applied?: boolean }> {
    if (params.type_mouvement === 'sortie') {
      return this.sortieBonLivraisonByBarcode({
        entrepot_id: params.entrepot_id,
        reference_document: params.reference_document,
        lignes: params.lignes,
      });
    }
    return this.entreeByBarcode({
      entrepot_id: params.entrepot_id,
      reference_document: params.reference_document,
      lignes: params.lignes,
    });
  }

  /** @deprecated Utiliser entreeByBarcode / mouvementByBarcode */
  createReception(body: {
    entrepot_id: number;
    document_libelle?: string | null;
    details: Array<{
      produits_id?: number | null;
      qnt: number;
      libelle?: string | null;
      ref_interne?: string | null;
      ref_externe?: string | null;
      code_barre?: string | null;
    }>;
  }): Observable<{ success: true; reception: unknown }> {
    return this.entreeByBarcode({
      entrepot_id: body.entrepot_id,
      reference_document: body.document_libelle,
      lignes: (body.details || []).map((d) => ({
        code_barre: d.code_barre,
        ref_interne: d.ref_interne || d.ref_externe,
        qnt: d.qnt,
        libelle: d.libelle,
      })),
    }).pipe(map((r) => ({ success: true as const, reception: r })));
  }

  /**
   * GET /api/stocks/by-code-barre?code_barre=&entrepot_id=
   */
  getStockByBarcode(
    codeBarre: string,
    entrepotId?: number | null
  ): Observable<number> {
    const cb = this.cleanBarcode(codeBarre);
    if (!cb) {
      return of(0);
    }
    return this.ensureSession().pipe(
      switchMap(() => {
        let params = new HttpParams().set('code_barre', cb);
        const eid = Number(entrepotId);
        if (Number.isFinite(eid) && eid > 0) {
          params = params.set('entrepot_id', String(eid));
        }
        const url = `${lcApiBase(this.authService)}/api/stocks/by-code-barre`;
        return this.http
          .get<{ success?: boolean; data?: { total_quantity?: number; stock_physique?: number } }>(
            url,
            { headers: this.authHeaders(), params }
          )
          .pipe(
            map((res) => {
              const n = Number(res?.data?.total_quantity ?? res?.data?.stock_physique ?? 0);
              return Number.isFinite(n) ? n : 0;
            }),
            catchError(() => of(0))
          );
      }),
      catchError(() => of(0))
    );
  }

  /**
   * POST /api/stocks/totals-by-code-barres
   */
  getTotalsByCodeBarres(
    codeBarres: string[],
    entrepotId?: number | null
  ): Observable<Record<string, number>> {
    const codes = [
      ...new Set((codeBarres || []).map((c) => this.cleanBarcode(c)).filter((c) => !!c)),
    ];
    if (!codes.length) {
      return of({});
    }
    return this.ensureSession().pipe(
      switchMap(() => {
        const body: Record<string, unknown> = { code_barres: codes };
        const eid = Number(entrepotId);
        if (Number.isFinite(eid) && eid > 0) {
          body.entrepot_id = eid;
        }
        const url = `${lcApiBase(this.authService)}/api/stocks/totals-by-code-barres`;
        return this.http
          .post<{ success?: boolean; data?: { totals?: Record<string, number> } }>(
            url,
            body,
            { headers: this.authHeaders() }
          )
          .pipe(
            map((res) => {
              const out: Record<string, number> = {};
              const raw = res?.data?.totals ?? {};
              for (const code of codes) {
                const n = Number(raw[code]);
                out[code] = Number.isFinite(n) ? n : 0;
              }
              return out;
            }),
            catchError(() => {
              const empty: Record<string, number> = {};
              for (const code of codes) {
                empty[code] = 0;
              }
              return of(empty);
            })
          );
      }),
      catchError(() => {
        const empty: Record<string, number> = {};
        for (const code of codes) {
          empty[code] = 0;
        }
        return of(empty);
      })
    );
  }

  private authHeaders(): HttpHeaders | undefined {
    const token = this.authService.getLogicielCaisseToken();
    if (!token?.trim()) {
      return undefined;
    }
    return new HttpHeaders({ Authorization: `Bearer ${token.trim()}` });
  }

  private cleanBarcode(value: unknown): string {
    const s = value != null ? String(value).replace(/\s/g, '').trim() : '';
    if (!s || s === '0' || s === '-' || s === '—') {
      return '';
    }
    return s;
  }

  private cleanRef(value: unknown): string {
    const s = value != null ? String(value).trim() : '';
    if (!s || s === '-' || s === '—' || s === 'null') {
      return '';
    }
    return s;
  }

  private ensureSession(): Observable<boolean> {
    if (this.authService.isAuthenticated() && this.authService.getBackStationId()) {
      return of(true);
    }
    return this.authService.bootstrap().pipe(
      map((status) => status === 'ready' && !!this.authService.getBackStationId()),
      switchMap((ok) =>
        ok
          ? of(true)
          : throwError(() => new Error('Connectez le module logiciel caisse pour alimenter le stock.'))
      ),
      catchError(() => throwError(() => new Error('Session logiciel caisse indisponible.')))
    );
  }
}
